import { useEffect, useRef, useState } from "react";
import { AlertCircle, Lock, PenLine, Wind, Snowflake, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt } from "@/lib/catalog";
import StepEnvironment from "./StepEnvironment";

import { tr } from "@/lib/i18n";
const TERRAIN = [["II", tr("Riva del lago")], ["IIa", tr("Terreno pianeggiante")], ["III", tr("Villaggi, campi aperti")], ["IV", tr("Grandi aree urbane")]];
const SURR = [[1.0, tr("Normale")], [0.8, tr("Esposto al vento")], [1.2, tr("Riparato")]];
const cls = "w-full h-10 px-3 text-sm font-tech border rounded-md outline-none bg-white focus:border-[#0F52BA]";

const Row = ({ label, req, missing, kind, children }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">
      {label}{req && <span className="text-slate-400">*</span>}
      {kind === "auto" && <Lock className="w-3 h-3" title={tr("Valore calcolato")} />}
      {kind === "manual" && <PenLine className="w-3 h-3" title={tr("Inserimento manuale")} />}
    </span>
    <div className={`mt-1 relative ${missing ? "[&>*]:border-amber-500" : "[&>*]:border-slate-200"}`}>
      {children}
      {missing && <AlertCircle className="w-4 h-4 text-amber-600 absolute right-8 top-3" data-testid={`loads-missing-${label}`} />}
    </div>
  </label>
);

const Locked = ({ v, unit, testid }) => (
  <div className={`${cls} flex items-center bg-slate-50 text-slate-700 border-slate-200`} data-testid={testid}>{v == null ? "—" : `${fmt(v, 2)} ${unit}`}</div>
);

// rilevamento automatico qp0 / Δh: poligoni ufficiali SIA 261 nel punto del progetto, tabella comuni CH, dataset importato
function GeoZone({ p, E, set }) {
  const [info, setInfo] = useState(null);
  const [csv, setCsv] = useState("");
  const isAdmin = useAuth().user?.role === "admin";
  const apply = (m) => set({ ...(m.qp0 != null ? { qp0: m.qp0 } : {}), ...(m.snow_dh != null ? { snow_dh: m.snow_dh } : {}), geo_source: `${m.municipality} · ${m.source}` });
  const detect = (fill = true) => api.get("/geo-zones/lookup", { params: { q: p.address || "", lat: p.lat ?? undefined, lng: p.lng ?? undefined } }).then(({ data }) => {
    setInfo((prev) => ({ ...data, imported: prev?.imported }));
    if (data.match && fill) apply(data.match);
  }).catch(() => setInfo({ error: true }));
  useEffect(() => { if (p.address || p.lat) detect(E.qp0 == null || E.snow_dh == null); }, [p.address, p.lat, p.lng]); // eslint-disable-line
  const imp = async () => {
    try { const { data } = await api.post("/geo-zones/import", { csv }); setInfo((prev) => ({ ...prev, imported: data })); setCsv(""); detect(E.qp0 == null || E.snow_dh == null); } catch { setInfo({ error: true }); }
  };
  const m = info?.match;
  const detected = m && E.snow_dh === m.snow_dh && (m.qp0 == null || E.qp0 === m.qp0);
  const extra = (
    <>
      {m?.qp0_range && (
        <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 p-2 text-xs flex flex-wrap items-center gap-1.5" data-testid="loads-geo-range">
          {tr("Zona di transizione del vento: qp0 tra")} <b>{fmt(m.qp0_range[0], 1)}</b> e <b>{fmt(m.qp0_range[1], 1)} {tr("kN/m²")}</b> {tr("— valore da stabilire per il sito:")}
          {m.qp0_range.map((v) => <button key={v} type="button" onClick={() => set({ qp0: v })} data-testid={`loads-geo-range-${v}`} className={`h-6 px-2 rounded border font-tech ${E.qp0 === v ? "bg-[#0F52BA] text-white border-[#0F52BA]" : "border-amber-400 bg-white"}`}>{fmt(v, 1)}</button>)}
        </div>
      )}
      <CommuneTable onPick={apply} total={info?.communes} />
      {isAdmin && <details><summary className="cursor-pointer text-xs font-medium" data-testid="loads-geo-import-toggle">{tr("Importa una tabella propria (CSV: comune;cantone;qp0;dh;fonte) — ha la precedenza")}</summary>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="loads-geo-csv" rows={4} className="w-full mt-2 p-2 text-xs font-tech border border-sky-200 rounded bg-white" placeholder={"comune;cantone;qp0;dh;fonte\nLuzern;LU;0.9;200;SIA 261:2020 Anhang D/E"} />
        <button type="button" onClick={imp} data-testid="loads-geo-import-btn" className="mt-1 h-8 px-3 rounded bg-[#0F52BA] text-white text-xs">{tr("Importa e rileva")}</button>
        {info?.imported && <span className="ml-2 text-xs" data-testid="loads-geo-import-result">{tr("Importati")} {info.imported.imported}{tr(", errori")} {info.imported.errors.length}</span>}
      </details>}
    </>
  );
  if (detected) return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-2" data-testid="loads-geo-detected">
      <div>{tr("La zona con carico di vento/neve è stata rilevata automaticamente:")} <b>{m.municipality}{m.canton ? ` (${m.canton})` : ""}</b> {tr("· qp0")} {m.qp0 != null ? `${m.qp0} kN/m²` : tr("da scegliere")}, Δh {m.snow_dh} m. <span className="text-emerald-700 text-xs">{tr("Fonte:")} {m.source}</span>. {tr("I valori restano modificabili.")}</div>
      {extra}
    </div>
  );
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 space-y-2" data-testid="loads-geo-note">
      <div className="flex gap-2"><Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>{info && !m ? tr("Posizione fuori dalle zone SIA 261 e comune non trovato.") : m ? tr("Zona SIA 261 ({0}): qp0 {1} / Δh {2} — i valori inseriti a mano sono diversi e vengono mantenuti.", [m.municipality, m.qp0 ?? "—", m.snow_dh ?? "—"]) : tr("Rilevamento automatico dalle zone ufficiali SIA 261.")} {tr("Senza corrispondenza qp0 e Δh si inseriscono")} <b>manualmente</b>{tr("; nessun valore viene inventato.")}</div></div>
      {extra}
    </div>
  );
}

// tabella ufficiale di tutti i comuni svizzeri con qp0 e Δh SIA 261
function CommuneTable({ onPick, total }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRows(null); return; }
    const t = setTimeout(() => api.get("/geo-zones/communes", { params: { q, limit: 30 } }).then(({ data }) => setRows(data)).catch(() => setRows(null)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <details data-testid="loads-commune-table"><summary className="cursor-pointer text-xs font-medium">{tr("Tabella comuni SIA 261")}{total ? tr(" ({0} comuni)", [total]) : ""}</summary>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Cerca comune o NPA…")} data-testid="loads-commune-search" className="w-full mt-2 h-8 px-2 text-xs border border-sky-200 rounded bg-white outline-none" />
      {rows && (
        <div className="mt-1 max-h-56 overflow-y-auto bg-white border border-sky-100 rounded">
          <table className="w-full text-xs font-tech">
            <thead className="text-slate-400 sticky top-0 bg-white"><tr><th className="text-left p-1.5">{tr("Comune")}</th><th>{tr("Cant.")}</th><th>qp0</th><th>Δh</th><th /></tr></thead>
            <tbody>
              {rows.rows.map((c) => (
                <tr key={c.bfs} className="border-t border-slate-100" data-testid={`loads-commune-${c.bfs}`}>
                  <td className="p-1.5">{c.municipality}</td><td className="text-center">{c.canton}</td>
                  <td className="text-center">{c.qp0 ?? (c.qp0_range ? c.qp0_range.join("–") : "—")}</td><td className="text-center">{c.snow_dh ?? "—"}</td>
                  <td className="text-right pr-1.5"><button type="button" onClick={() => onPick(c)} data-testid={`loads-commune-pick-${c.bfs}`} className="h-6 px-2 rounded bg-[#0F52BA] text-white">{tr("Usa")}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.matched > rows.rows.length && <div className="p-1.5 text-[11px] text-slate-400">{rows.matched - rows.rows.length} {tr("altri risultati: affina la ricerca.")}</div>}
        </div>
      )}
    </details>
  );
}

function LoadsCH({ p, update }) {
  const E = p.engine || {};
  const set = (patch) => update((prev) => ({ engine: { ...(prev.engine || {}), country: "CH", ...patch } }));
  const [res, setRes] = useState(null);
  const tm = useRef();
  const z = E.z ?? p.building_height;
  useEffect(() => {
    clearTimeout(tm.current);
    tm.current = setTimeout(() => {
      api.post("/engine/loads", { country: "CH", altitude: p.altitude, z, terrain: E.terrain || "III", qp0: E.qp0, snow_dh: E.snow_dh, Ce: E.Ce ?? 1, pitch: 0,
        consequence_class: E.consequence_class, service_life: E.service_life }).then((r) => setRes(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(tm.current);
  }, [p.altitude, z, E.terrain, E.qp0, E.snow_dh, E.Ce, E.consequence_class, E.service_life]);
  const miss = (f) => res?.missing?.some((m) => m.field === f);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-[420px_1fr] gap-8" data-testid="loads-ch">
      <div className="space-y-4">
        <Row label={tr("Sistema di calcolo")} kind="auto">
          <select className={cls} value="CH" data-testid="loads-system" onChange={(e) => e.target.value === "IT" && update((prev) => ({ engine: { ...(prev.engine || {}), country: "IT" } }))}>
            <option value="CH">{tr("SIA 260 / SIA 261 · SN EN (Svizzera)")}</option><option value="IT">{tr("NTC 2018 (Italia)")}</option>
          </select>
        </Row>
        <Row label={tr("Sede")} kind="auto"><div className={`${cls} flex items-center bg-slate-50 truncate`} data-testid="loads-address">{p.address || "—"}</div></Row>
        <Row label={tr("Tipo di inserimento")} kind="manual"><div className={`${cls} flex items-center bg-slate-50`}>{tr("Zona (valori dalle mappe SIA 261)")}</div></Row>
        <Row label={tr("Altitudine del terreno")} req kind={E.altitude_manual ? "manual" : "auto"}>
          <input type="number" className={cls} value={p.altitude ?? ""} data-testid="loads-altitude"
            onChange={(e) => { update({ altitude: Number(e.target.value) }); set({ altitude_manual: true }); }} />
        </Row>
        <Row label={tr("Classe di conseguenza")} req missing={miss("consequence_class")} kind="manual">
          <select className={cls} value={E.consequence_class || ""} data-testid="loads-cc" onChange={(e) => set({ consequence_class: e.target.value || null })}>
            <option value="" /><option>CC1</option><option>CC2</option><option>CC3</option>
          </select>
        </Row>
        <Row label={tr("Vita utile")} req missing={miss("service_life")} kind="manual">
          <select className={cls} value={E.service_life || ""} data-testid="loads-life" onChange={(e) => set({ service_life: e.target.value ? Number(e.target.value) : null })}>
            <option value="" /><option value="25">{tr("25 anni")}</option><option value="50">{tr("50 anni")}</option>
          </select>
        </Row>
        <div className="pt-2 flex items-center gap-2 font-display font-semibold"><Wind className="w-4 h-4 text-[#0F52BA]" />{tr("Carico di vento")}</div>
        <Row label={tr("Pressione dinamica di riferimento qp0 [kN/m²]")} req missing={miss("qp0")} kind="manual">
          <input type="number" step="0.1" className={cls} value={E.qp0 ?? ""} data-testid="loads-qp0" onChange={(e) => set({ qp0: e.target.value === "" ? null : Number(e.target.value) })} />
        </Row>
        <div className="flex gap-1.5">{[0.9, 1.1, 1.3].map((v) => (
          <button key={v} type="button" data-testid={`loads-qp0-${v}`} onClick={() => set({ qp0: v })}
            className={`h-8 px-2.5 text-xs font-tech rounded border ${E.qp0 === v ? "bg-[#0F52BA] text-white border-[#0F52BA]" : "border-slate-200"}`}>{fmt(v, 1)}</button>
        ))}<span className="text-[11px] text-slate-400 self-center">{tr("classi generali della mappa SIA 261")}</span></div>
        <Row label={tr("Categoria di terreno")} req kind="manual">
          <select className={cls} value={E.terrain || "III"} data-testid="loads-terrain" onChange={(e) => set({ terrain: e.target.value })}>
            {TERRAIN.map(([k, l]) => <option key={k} value={k}>{l} ({k})</option>)}
          </select>
        </Row>
        <Row label={tr("Altezza di riferimento z [m]")} kind={E.z != null ? "manual" : "auto"}>
          <input type="number" className={cls} value={z ?? ""} data-testid="loads-z" onChange={(e) => set({ z: e.target.value === "" ? null : Number(e.target.value) })} />
        </Row>
        <Row label={tr("Pressione dinamica di picco qp")} kind="auto"><Locked v={res?.qp} unit={tr("kN/m²")} testid="loads-qp" /></Row>
        <div className="pt-2 flex items-center gap-2 font-display font-semibold"><Snowflake className="w-4 h-4 text-[#0F52BA]" />{tr("Carico di neve")}</div>
        <Row label={tr("Correzione altitudine Δh [m]")} req missing={miss("snow_dh")} kind="manual">
          <input type="number" step="100" className={cls} value={E.snow_dh ?? ""} data-testid="loads-dh" onChange={(e) => set({ snow_dh: e.target.value === "" ? null : Number(e.target.value) })} />
        </Row>
        <Row label={tr("Dintorni")} kind="manual">
          <select className={cls} value={E.Ce ?? 1} data-testid="loads-surroundings" onChange={(e) => set({ Ce: Number(e.target.value) })}>
            {SURR.map(([v, l]) => <option key={v} value={v}>{l} {tr("(Ce =")} {v})</option>)}
          </select>
        </Row>
        <Row label={tr("Carico di neve al suolo sk")} kind="auto"><Locked v={res?.sk} unit={tr("kN/m²")} testid="loads-sk" /></Row>
      </div>
      <div className="space-y-4">
        <GeoZone p={p} E={E} set={set} />
        {res?.missing?.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="loads-missing">
            <div className="font-medium mb-1">{tr("Dati obbligatori mancanti")}</div>
            <ul className="list-disc pl-5">{res.missing.map((m) => <li key={m.field}>{m.reason}</li>)}</ul>
          </div>
        )}
        {res?.warnings?.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-900 space-y-1" data-testid="loads-warnings">{res.warnings.map((w) => <div key={w}>{w}</div>)}</div>
        )}
        <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="loads-trace">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">{tr("Norme ·")} {res?.standards ? Object.values(res.standards).join(" · ") : "—"}</div>
          <table className="w-full text-xs"><tbody>{(res?.trace || []).map((s) => (
            <tr key={s.id} className="border-t border-slate-100 align-top">
              <td className="py-1.5 pr-2 font-medium">{s.title}</td><td className="font-tech text-slate-500 pr-2">{s.formula}</td>
              <td className="font-tech font-semibold whitespace-nowrap">{fmt(s.value, 3)} {s.unit}</td>
            </tr>))}</tbody></table>
          <p className="text-[11px] text-slate-400 mt-2">{tr("Valori calcolati dal motore backend (bloccati). Le zone di bordo/angolo sono visibili nel campo moduli (pulsante \"zone vento\").")}</p>
        </div>
      </div>
    </div>
  );
}

export default function StepLoads(props) {
  const c = props.p.engine?.country || (props.p.region ? "IT" : "CH");
  if (c === "IT") return (
    <div>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-6">
        <button data-testid="loads-switch-ch" className="text-sm text-[#0F52BA]" onClick={() => props.update((prev) => ({ engine: { ...(prev.engine || {}), country: "CH" } }))}>{tr("Passa a SIA 261 (Svizzera)")}</button>
      </div>
      <StepEnvironment {...props} />
    </div>
  );
  return <LoadsCH {...props} />;
}
